import { useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import config from "../config/index.js";
import { getReadProvider } from "../services/web3.js";
import { fetchGilHolderRank, fetchTvtZone } from "../services/api.js";
import { useWallet } from "../context/WalletContext.jsx";

/**
 * The connected wallet's $GILD position: raw balance, its USD value at the
 * live spot price, and where the wallet sits in the holder leaderboard.
 *
 * §M6 (Jul 2026 client review) — "Would be good to add 'Current holding'
 * i.e. Balance: 59,690,482,625.074 value $74,987. And also what ranking
 * they are of non-protocol owned wallets 'You are currently 6 of 167
 * wallets'." Also: "Currently it says tokens but shows number of TD's" —
 * the sidebar's Investment Summary row now reads this hook instead of
 * `deposits.length`.
 *
 * Balance comes straight off the ERC-20 (the user's own balance is a cheap,
 * always-correct read). Price comes from /api/tvt/zone — the same spot the
 * treasury executor classifies zones with, so the value here can't disagree
 * with the Market Zone card. Rank comes from the backend holder index.
 */
export function useGilHolding({ pollMs = 30_000, neighbours = 2 } = {}) {
    const [balance, setBalance] = useState(null); // BigInt (18dp) | null
    const [spot, setSpot] = useState(null); // USDC per GIL, plain float
    const [ranking, setRanking] = useState(null);

    // Live spot price — wallet-independent, so it loads even for guests.
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const z = await fetchTvtZone();
                if (cancelled) return;
                const raw = Number(z?.spotPrice || 0);
                // TreasuryExecutor scale: USDC-per-GIL × 1e8.
                setSpot(raw > 0 ? raw / 1e8 : null);
            } catch {
                /* keep last value */
            }
        }
        load();
        const id = setInterval(load, pollMs);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [pollMs]);

    // Wallet balance + leaderboard position.
    const { address } = useWallet();
    useEffect(() => {
        if (!address || !config.contracts.gil) {
            setBalance(null);
            setRanking(null);
            return undefined;
        }
        let cancelled = false;
        const gil = new ethers.Contract(
            config.contracts.gil,
            ["function balanceOf(address) view returns (uint256)"],
            getReadProvider()
        );
        async function load() {
            try {
                const bal = await gil.balanceOf(address);
                if (!cancelled) setBalance(BigInt(bal));
            } catch {
                /* silent — RPC blip, keep last value */
            }
            try {
                const r = await fetchGilHolderRank(address, { neighbours });
                if (!cancelled) setRanking(r);
            } catch {
                // The holder index is a nice-to-have; a failure here must
                // never blank the balance the user actually cares about.
                if (!cancelled) setRanking(null);
            }
        }
        load();
        const id = setInterval(load, pollMs);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [address, pollMs, neighbours]);

    const valueUsd = useMemo(() => {
        if (balance == null || spot == null) return null;
        // 18-dp BigInt → float. Precision loss here is irrelevant at USD
        // display granularity, and the balance itself is still rendered
        // from the BigInt.
        return (Number(balance) / 1e18) * spot;
    }, [balance, spot]);

    return { balance, spot, valueUsd, ranking };
}
